import type { AcquisitionFileFormatRule } from '@steerfi/shared/types/jsonb/integration-profile.types';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { type FileFormatRow, listFileFormats } from '@/api/file-formats';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { focusAfterRemoval } from '@/hooks/focus-after-removal';

interface FileFormatPickerProps {
  value: AcquisitionFileFormatRule[];
  onChange: (rules: AcquisitionFileFormatRule[]) => void;
  partyId?: string;
  disabled?: boolean;
}

const isValidCron = (expr: string): boolean => {
  const parts = expr.trim().split(/\s+/);
  return parts.length === 5 && parts.every((p) => /^[\d,\-/*]+$/.test(p));
};

const newRule = (): AcquisitionFileFormatRule => ({
  fileFormatId: 0,
  remoteDirectoryPath: '/inbound',
  enabled: true,
});

export const FileFormatPicker: React.FC<FileFormatPickerProps> = ({
  value,
  onChange,
  partyId,
  disabled,
}) => {
  const [fileFormats, setFileFormats] = useState<FileFormatRow[]>([]);
  const [cronErrors, setCronErrors] = useState<Record<number, string>>({});
  const [timezoneErrors, setTimezoneErrors] = useState<Record<number, string>>({});
  const isDisabled = disabled ?? false;
  const ruleIds = useRef<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  while (ruleIds.current.length < value.length) {
    ruleIds.current.push(crypto.randomUUID());
  }

  useEffect(() => {
    listFileFormats({
      pageSize: 100,
      ...(partyId ? { party_id: partyId, scope: 'provider' } : {}),
    })
      .then((res) => setFileFormats(res.data))
      .catch(() => {});
  }, [partyId]);

  const addRule = () => {
    ruleIds.current.push(crypto.randomUUID());
    onChange([...value, newRule()]);
  };

  const removeRule = (idx: number) => {
    focusAfterRemoval(rootRef.current, ruleIds.current, idx, () => {
      ruleIds.current.splice(idx, 1);
      onChange(value.filter((_, i) => i !== idx));
      setCronErrors({});
      setTimezoneErrors({});
    });
  };

  const updateRule = (idx: number, patch: Partial<AcquisitionFileFormatRule>) => {
    onChange(value.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const clearRuleField = (idx: number, field: 'scheduleCron' | 'timezone') => {
    onChange(
      value.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r };
        delete next[field];
        return next;
      })
    );
  };

  return (
    <div ref={rootRef} className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">
          Acquisition rules <span className="text-destructive">*</span>
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          disabled={isDisabled}
          data-add-row=""
          onClick={addRule}
        >
          <IconPlus size={12} />
          Add rule
        </Button>
      </div>
      {value.length === 0 && <p className="text-xs text-muted-foreground italic">No rules yet.</p>}
      <div className="space-y-2">
        {value.map((rule, idx) => (
          <div key={ruleIds.current[idx]} className="rounded-lg border p-3 space-y-2">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">
                File format <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Select
                  disabled={isDisabled}
                  value={rule.fileFormatId ? String(rule.fileFormatId) : ''}
                  onValueChange={(v) => updateRule(idx, { fileFormatId: Number(v) })}
                >
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Select file format…" />
                  </SelectTrigger>
                  <SelectContent>
                    {fileFormats.map((ff) => (
                      <SelectItem key={ff.id} value={String(ff.id)} className="text-xs">
                        {ff.code} — {ff.format_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                  disabled={isDisabled}
                  aria-label={`Remove rule ${idx + 1}`}
                  data-remove-row={ruleIds.current[idx]}
                  onClick={() => removeRule(idx)}
                >
                  <IconTrash size={14} aria-hidden />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Override cron</Label>
                <Input
                  className="h-7 text-xs font-mono"
                  disabled={isDisabled}
                  placeholder="(inherits profile-level)"
                  value={rule.scheduleCron ?? ''}
                  aria-invalid={!!cronErrors[idx]}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCronErrors((prev) => {
                      const n = { ...prev };
                      delete n[idx];
                      return n;
                    });
                    if (val) updateRule(idx, { scheduleCron: val });
                    else clearRuleField(idx, 'scheduleCron');
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isValidCron(val)) {
                      setCronErrors((prev) => ({
                        ...prev,
                        [idx]: 'Must be a valid 5-field cron expression (e.g. 0 7 * * *)',
                      }));
                    }
                  }}
                />
                {cronErrors[idx] && (
                  <p className="text-[11px] text-destructive">{cronErrors[idx]}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Override timezone</Label>
                <Input
                  className="h-7 text-xs"
                  disabled={isDisabled}
                  placeholder="(inherits profile-level)"
                  value={rule.timezone ?? ''}
                  aria-invalid={!!timezoneErrors[idx]}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTimezoneErrors((prev) => {
                      const n = { ...prev };
                      delete n[idx];
                      return n;
                    });
                    if (val) updateRule(idx, { timezone: val });
                    else clearRuleField(idx, 'timezone');
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && val !== 'UTC' && !Intl.supportedValuesOf('timeZone').includes(val)) {
                      setTimezoneErrors((prev) => ({
                        ...prev,
                        [idx]: 'Must be a valid IANA timezone (e.g. America/New_York, UTC)',
                      }));
                    }
                  }}
                />
                {timezoneErrors[idx] && (
                  <p className="text-[11px] text-destructive">{timezoneErrors[idx]}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
